// ===================================
// USER SETTINGS PAGE
// src/pages/UserSettings.js
// Stage 11: User preferences & profile
// ===================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './UserSettings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const UserSettings = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Profile form
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    username: ''
  });

  // Password form
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  // Preferences
  const [preferences, setPreferences] = useState({
    defaultDuration: '30',
    defaultPersonality: 'friendly',
    defaultLevel: 'intermediate',
    emailNotifications: true,
    showTours: true,
    darkMode: true
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        username: user.username || ''
      });
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update profile');
      }

      const data = await response.json();
      if (updateUser) {
        updateUser(data.user);
      }
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      setError('New passwords do not match');
      return;
    }

    if (passwords.new.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to change password');
      }

      setPasswords({ current: '', new: '', confirm: '' });
      setSuccess('Password changed successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      // Store tour preference locally
      localStorage.setItem('showTours', preferences.showTours);
      
      setSuccess('Preferences saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestartTours = () => {
    localStorage.removeItem('tour_welcome_completed');
    localStorage.removeItem('tour_booking_completed');
    localStorage.removeItem('tour_interview_completed');
    localStorage.removeItem('tour_progress_completed');
    setSuccess('Tours have been reset. They will appear again on relevant pages.');
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'tours', label: 'Tours & Help', icon: '🎯' }
  ];

  return (
    <div className="user-settings-page">
      <div className="settings-container">
        {/* Header */}
        <div className="page-header">
          <h1>⚙️ Settings</h1>
          <p>Manage your account and preferences</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <span>⚠️</span> {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success">
            <span>✓</span> {success}
          </div>
        )}

        <div className="settings-layout">
          {/* Tabs */}
          <div className="settings-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="settings-content">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="settings-section">
                <h2>Profile Information</h2>
                <p className="section-description">Update your personal information</p>

                <form onSubmit={handleProfileUpdate} className="settings-form">
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={profile.username}
                      onChange={(e) => setProfile(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="username"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="settings-section">
                <h2>Change Password</h2>
                <p className="section-description">Keep your account secure</p>

                <form onSubmit={handlePasswordChange} className="settings-form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="settings-section">
                <h2>Interview Defaults</h2>
                <p className="section-description">Set your default interview preferences</p>

                <div className="settings-form">
                  <div className="form-group">
                    <label>Default Duration</label>
                    <select
                      value={preferences.defaultDuration}
                      onChange={(e) => setPreferences(prev => ({ ...prev, defaultDuration: e.target.value }))}
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Default Interviewer Style</label>
                    <select
                      value={preferences.defaultPersonality}
                      onChange={(e) => setPreferences(prev => ({ ...prev, defaultPersonality: e.target.value }))}
                    >
                      <option value="friendly">Friendly</option>
                      <option value="strict">Strict</option>
                      <option value="technical">Technical</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Default Experience Level</label>
                    <select
                      value={preferences.defaultLevel}
                      onChange={(e) => setPreferences(prev => ({ ...prev, defaultLevel: e.target.value }))}
                    >
                      <option value="entry">Entry Level</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>

                  <div className="form-divider"></div>

                  <h3>Notifications</h3>

                  <div className="form-group toggle-group">
                    <label>Email Notifications</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.emailNotifications}
                        onChange={(e) => setPreferences(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handlePreferencesSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            )}

            {/* Tours & Help Tab */}
            {activeTab === 'tours' && (
              <div className="settings-section">
                <h2>Guided Tours</h2>
                <p className="section-description">Control the in-app guided tours</p>

                <div className="settings-form">
                  <div className="form-group toggle-group">
                    <div>
                      <label>Show Guided Tours</label>
                      <p className="form-hint">Display helpful tours when you visit new features</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.showTours}
                        onChange={(e) => setPreferences(prev => ({ ...prev, showTours: e.target.checked }))}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="tour-actions">
                    <h4>Reset Tours</h4>
                    <p>If you want to see the guided tours again, you can reset them:</p>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handleRestartTours}
                    >
                      🔄 Restart All Tours
                    </button>
                  </div>

                  <div className="help-section">
                    <h4>Need Help?</h4>
                    <div className="help-links">
                      <a href="/app/book" className="help-link">
                        <span className="help-icon">📅</span>
                        <div>
                          <strong>Book Your First Session</strong>
                          <p>Start practicing with our AI interviewer</p>
                        </div>
                      </a>
                      <a href="/app/progress" className="help-link">
                        <span className="help-icon">📊</span>
                        <div>
                          <strong>Track Your Progress</strong>
                          <p>View your scores and achievements</p>
                        </div>
                      </a>
                    </div>
                  </div>

                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handlePreferencesSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
